import type { AtlasPinPosition } from '@atlas/api-contract';
import { create } from 'zustand';

/**
 * Where every drawn pin currently is, in iframe viewport coordinates.
 *
 * ============================================================================
 * WHY THIS IS NOT IN `viewer-store`
 * ============================================================================
 * It is the one piece of viewer state that changes sixty times a second. The
 * agent throttles `atlas:pin-positions` to one message per animation frame, and
 * during a scroll that is a write per frame — which in `viewer-store` would
 * re-render the toolbar, the screen dropdown, the width control and the whole
 * thread panel on every frame of every scroll, for a number none of them read.
 *
 * Kept apart, exactly two things subscribe: the open popover and the pin hit
 * layer. Everything else in the viewer is untouched by a scroll, which is what
 * makes the popover able to stay glued without the rest of the app paying for
 * it.
 *
 * The map is replaced wholesale on every message rather than merged, because
 * `atlas:pin-positions` is a complete statement about the pins currently drawn:
 * a pin missing from it is a pin the agent is no longer rendering, and merging
 * would keep a stale marker alive at its last known spot.
 */

export type PinPositionMap = ReadonlyMap<string, AtlasPinPosition>;

export interface PinPositionsState {
  positions: PinPositionMap;
  /** Replace the whole set, as reported by one `atlas:pin-positions`. */
  setPositions: (positions: readonly AtlasPinPosition[]) => void;
  /**
   * Forget everything. Called when the framed document is replaced: those
   * coordinates described a page that no longer exists, and a popover glued to
   * one of them would sit over the new screen pointing at nothing.
   */
  clear: () => void;
}

const EMPTY: PinPositionMap = new Map();

export const usePinPositionsStore = create<PinPositionsState>()((set) => ({
  positions: EMPTY,

  setPositions: (positions) => {
    set({
      positions: new Map(
        positions.map((position) => [position.threadId, position]),
      ),
    });
  },

  clear: () => {
    set((state) => (state.positions.size === 0 ? state : { positions: EMPTY }));
  },
}));

/**
 * One pin's position, or `null` when the agent is not reporting it.
 *
 * A selector rather than a hook over the whole map so a component watching one
 * pin re-renders when *that* pin moves, not when any of forty do.
 */
export const selectPinPosition =
  (threadId: string | null) =>
  (state: PinPositionsState): AtlasPinPosition | null =>
    threadId === null ? null : (state.positions.get(threadId) ?? null);
